import { useMemo, useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { AutocompleteProps, FormHelperText, Paper, PaperProps } from '@mui/material';
import AutocompleteComponent from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import parse from 'autosuggest-highlight/parse';

import { debounce, noop } from '@altirllc/shared/utils/functionalUtils';

import { ensureGoogleMaps } from '@/scripts/googleMaps';

import { SimpleError } from '../formik/error/SimpleError';
import { Location } from '../icons/Location';
import { TMatch, TServices, TPlaceSuggestion, TPlace, TSearchResult } from './types';
import { formatAddress } from './utils';

const services: TServices = {
  autocomplete: null,
  places: null,
  token: undefined,
};

type TPlacesAutoSuggestProps = Omit<
  AutocompleteProps<unknown, false, true, true>,
  'renderInput' | 'options'
> & {
  name: string;
  label: string;
  'data-testid'?: string;
  isFullAddressShown?: boolean;
  error?: string;
  initialInputValue?: string;
  setSearchResult?: (res: Partial<TSearchResult>, place?: google.maps.places.PlaceResult) => void;
};

export const PlacesAutosuggest = ({
  name,
  label: labelKey,
  placeholder,
  'data-testid': testId,
  isFullAddressShown = true,
  error,
  initialInputValue = '',
  setSearchResult = noop,
}: TPlacesAutoSuggestProps) => {
  const apiReady = useRef(false);
  const [inputValue, setInputValue] = useState(initialInputValue);
  const [options, setOptions] = useState<readonly TPlace[]>([]);
  const { t } = useTranslation();

  const label = t(labelKey);

  const onPlaceSelected = (event: unknown, newValue: TPlace | null | string) => {
    if (typeof newValue === 'string') {
      return;
    }

    if (newValue) {
      if (!services.places && window.google) {
        const container = document.createElement('div');
        services.places = new window.google.maps.places.PlacesService(container);
      }

      if (!services.places || !newValue.place_id) {
        return;
      }

      services.places.getDetails(
        {
          placeId: newValue?.place_id,
          fields: ['name', 'address_component', 'geometry'],
          sessionToken: services.token,
        },
        (place, status) => {
          if (status !== 'OK' || !place || !place.name || !place.address_components) {
            return;
          }

          setSearchResult(formatAddress(place), place);
        },
      );
    }
  };

  const fetchSuggestions = useMemo(
    () =>
      debounce(
        (
          request: { input: string; componentRestrictions: { country: string }; types: string[] },
          callback: (results: TPlaceSuggestion[] | null) => void,
        ) => {
          request.componentRestrictions = { country: 'us' };
          services.autocomplete?.getPlacePredictions(request, callback);
        },
        200,
      ),
    [],
  );

  /**
   * Loads suggestions for the current input text (debounced). `active` drops the results of a
   * lookup once the text has changed or the box unmounted: otherwise a lookup scheduled for older
   * text (e.g. right before the user clears the box) lands later and shows stale suggestions under
   * the new text, or updates an unmounted component.
   */
  useEffect(() => {
    if (!apiReady) {
      return;
    }

    if (inputValue === '') {
      setOptions([]);
      return;
    }

    let active = true;

    fetchSuggestions(
      { input: inputValue, componentRestrictions: { country: 'us' }, types: ['address'] },
      (results) => {
        if (active && results) {
          setOptions(results);
        }
      },
    );

    return () => {
      active = false;
    };
  }, [inputValue, fetchSuggestions]);

  useEffect(() => {
    ensureGoogleMaps().then(() => {
      services.autocomplete = new window.google.maps.places.AutocompleteService();
      services.token = new google.maps.places.AutocompleteSessionToken();

      apiReady.current = true;
    });
  }, []);

  useEffect(() => {
    setInputValue(initialInputValue);
  }, [initialInputValue]);

  const getOptionDescription = (option: string | TPlace) => {
    if (typeof option === 'string') {
      return option;
    }
    return isFullAddressShown ? option.description : option.structured_formatting.main_text;
  };

  return (
    <Box mb={2}>
      <AutocompleteComponent
        getOptionLabel={getOptionDescription}
        disableClearable
        forcePopupIcon={false}
        options={options}
        autoComplete
        includeInputInList
        inputValue={inputValue}
        freeSolo
        onChange={onPlaceSelected}
        onInputChange={(event, newInputValue) => {
          setInputValue(newInputValue);
          setSearchResult({ address: newInputValue });
        }}
        PaperComponent={(props: PaperProps) => (
          <Paper {...props} elevation={2} sx={{ ...props.sx, mt: 0.5, borderRadius: 2 }} />
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            placeholder={placeholder ? t(placeholder) : placeholder}
            fullWidth
            margin="none"
            variant="standard"
            name={name}
            error={Boolean(error)}
            inputProps={{
              ...params.inputProps,
              'data-testid': testId,
            }}
          />
        )}
        renderOption={(props, option) => {
          const matches = option.structured_formatting.main_text_matched_substrings;

          const parts = parse(
            option.structured_formatting.main_text,
            matches.map((match: TMatch) => [match.offset, match.offset + match.length]),
          );

          return (
            <li {...props} key={option.place_id}>
              <Grid container alignItems="center">
                <Grid item>
                  <Box component={Location} sx={{ color: 'text.secondary', mr: 2 }} />
                </Grid>
                <Grid item xs container gap={1} alignItems="center" sx={{ minHeight: '40px' }}>
                  <Grid item>
                    {parts.map((part) => (
                      <span
                        key={part.text}
                        style={{
                          fontWeight: part.highlight ? 600 : 400,
                        }}
                      >
                        {part.text}
                      </span>
                    ))}
                  </Grid>
                  <Grid item>
                    <Typography variant="body2" color="text.secondary">
                      {option.structured_formatting.secondary_text.replace(', USA', '')}
                    </Typography>
                  </Grid>
                </Grid>
              </Grid>
            </li>
          );
        }}
      />

      <FormHelperText component="div">
        <SimpleError error={error} errorsShown={!!error} label={label} />
      </FormHelperText>
    </Box>
  );
};
